import { motion, HTMLMotionProps } from "motion/react";
import { buttonHoverVariants } from "../../lib/animations";
import clsx from "clsx";
import { forwardRef, ReactNode } from "react";

interface ButtonProps extends Omit<HTMLMotionProps<"button">, "ref" | "children"> {
  variant?: "primary" | "secondary" | "ghost" | "danger";
  size?: "sm" | "md" | "lg";
  loading?: boolean;
  icon?: ReactNode;
  iconPosition?: "left" | "right";
  children?: ReactNode;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      variant = "primary",
      size = "md",
      loading = false,
      icon,
      iconPosition = "left",
      className,
      disabled,
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled || loading;

    return (
      <motion.button
        ref={ref}
        variants={buttonHoverVariants}
        whileHover={!isDisabled ? "hover" : undefined}
        whileTap={!isDisabled ? "tap" : undefined}
        disabled={isDisabled}
        className={clsx(
          "inline-flex items-center justify-center gap-2 font-medium rounded-xl transition-all duration-200",
          "focus:outline-none focus:ring-2 focus:ring-offset-2",
          // Sizes
          size === "sm" && "px-3 py-1.5 text-xs",
          size === "md" && "px-4 py-2.5 text-sm",
          size === "lg" && "px-6 py-3 text-base",
          // Variants
          variant === "primary" && [
            "gradient-brand text-white shadow-brand",
            "hover:shadow-brand-lg",
            "focus:ring-lime-500/50",
          ],
          variant === "secondary" && [
            "bg-[var(--bg-hover)] text-[var(--text-primary)]",
            "border-2 border-[var(--text-muted)]/30",
            "hover:bg-[var(--bg-active)] hover:border-lime-500/50",
            "focus:ring-lime-500/30",
          ],
          variant === "ghost" && [
            "bg-transparent text-[var(--text-secondary)]",
            "border border-[var(--border)]",
            "hover:bg-[var(--bg-hover)] hover:text-[var(--text-primary)] hover:border-[var(--text-muted)]/40",
            "focus:ring-lime-500/30",
          ],
          variant === "danger" && [
            "bg-rose-500/10 text-rose-500 border-2 border-rose-500/40",
            "hover:bg-rose-500 hover:text-white hover:border-rose-500",
            "focus:ring-rose-500/50",
          ],
          // States
          isDisabled && "opacity-50 cursor-not-allowed",
          className
        )}
        {...props}
      >
        {loading && (
          <svg
            className="animate-spin h-4 w-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        )}
        {!loading && icon && iconPosition === "left" && icon}
        {children}
        {!loading && icon && iconPosition === "right" && icon}
      </motion.button>
    );
  }
);

Button.displayName = "Button";
export default Button;
